import { useState, useEffect, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Users, IndianRupee, TrendingUp, Award, LogOut, 
  BarChart3, PieChart as PieChartIcon, Activity, ChevronRight, Search, User, Eye,
  Link2, Unlink, FlaskConical, FileText, Plus, Save, X, Edit, Trash2,
  Sparkles, BookOpen, Shield, Heart, Target, CheckCircle2, Circle,
  Crown, Clock, AlertTriangle
} from "lucide-react";
import { useAuth, apiCall } from "../App";
import { Badge } from "@/components/ui/badge";
import DemoVideoButton from "../components/DemoVideo";
import EngagementScores from "../components/EngagementScores";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, PieChart, Pie, Legend
} from "recharts";
import { toast } from "sonner";
import { AdminReportsPanel, ClientProfilingDownload } from "../components/ReportsPanel";
import DiagnosticResultsView from "../components/DiagnosticResultsView";
import KoshaQuizResultsView from "../components/KoshaQuizResultsView";
import { EscalationPanel, CoachJourneyView, InterventionPlanManager } from "../components/JourneyPanel";
const AdminSettingsPanel = lazy(() => import("../components/AdminSettingsPanel"));
const AdminCoachesPanel = lazy(() => import("../components/AdminCoachesPanel"));
const AdminExpertsPanel = lazy(() => import("../components/AdminExpertsPanel"));
const AdminPracticesPanel = lazy(() => import("../components/AdminPracticesPanel"));

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [stuckAssessments, setStuckAssessments] = useState([]);
  const [recomputing, setRecomputing] = useState(null);
  const [trends, setTrends] = useState(null);
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDetail, setClientDetail] = useState(null);
  const [clientAnalytics, setClientAnalytics] = useState(null);
  const [clientExpertMappings, setClientExpertMappings] = useState([]);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [assignCoachId, setAssignCoachId] = useState("");
  const [clientNotes, setClientNotes] = useState([]);
  const [adminNewNote, setAdminNewNote] = useState("");
  const [adminShowNoteForm, setAdminShowNoteForm] = useState(false);
  const [adminEditingNoteId, setAdminEditingNoteId] = useState(null);
  const [adminEditContent, setAdminEditContent] = useState("");
  const [adminNoteCategory, setAdminNoteCategory] = useState("General");
  const [adminEditCategory, setAdminEditCategory] = useState("General");
  const [adminFilterCategory, setAdminFilterCategory] = useState("All");
  const [editingClient, setEditingClient] = useState(null);
  const [editClientForm, setEditClientForm] = useState({});
  const [editClientSaving, setEditClientSaving] = useState(false);

  const NOTE_CATEGORIES = ["General", "Session Summary", "Action Items", "Follow-up", "Observation", "Milestone"];

  // Expert & Practice state (lists shared with child components)
  const [experts, setExperts] = useState([]);
  const [practices, setPractices] = useState([]);
  const [practiceCategories, setPracticeCategories] = useState([]);
  const [expertClientMappings, setExpertClientMappings] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [selectedForAssignment, setSelectedForAssignment] = useState([]);
  const [clientModule, setClientModule] = useState("relationship");
  const [clientPracticeAssignments, setClientPracticeAssignments] = useState([]);
  const [allPracticeAssignments, setAllPracticeAssignments] = useState([]);

  const fetchAssignments = async () => {
    const res = await apiCall("GET", "/admin/assignments", null, token);
    if (res.success) setAssignments(res.data.assignments || []);
  };

  const fetchCoaches = async () => {
    const res = await apiCall("GET", "/admin/coaches", null, token);
    if (res.success) setCoaches(res.data.coaches || []);
  };

  const fetchExperts = async () => {
    const res = await apiCall("GET", "/admin/experts", null, token);
    if (res.success) setExperts(res.data.experts || []);
  };

  const fetchExpertClientMappings = async () => {
    const res = await apiCall("GET", "/expert-client-map", null, token);
    if (res.success) setExpertClientMappings(res.data.mappings || []);
  };

  const fetchPractices = async () => {
    const res = await apiCall("GET", "/practices", null, token);
    if (res.success) {
      setPractices(res.data.practices || []);
      setPracticeCategories(res.data.categories || []);
    }
  };

  const fetchAllPracticeAssignments = async () => {
    const res = await apiCall("GET", "/practice-assignments/all", null, token);
    if (res.success) setAllPracticeAssignments(res.data.assignments || []);
  };

  useEffect(() => {
    const fetchData = async () => {
      const [metricsRes, trendsRes, clientsRes, stuckRes] = await Promise.all([
        apiCall("GET", "/admin/metrics", null, token),
        apiCall("GET", "/admin/trends", null, token),
        apiCall("GET", "/coach/clients", null, token),
        apiCall("GET", "/admin/stuck-assessments", null, token),
      ]);
      
      if (metricsRes.success) setMetrics(metricsRes.data);
      if (trendsRes.success) setTrends(trendsRes.data);
      if (stuckRes.success) setStuckAssessments(stuckRes.data.stuck || []);
      if (clientsRes.success) {
        const allUsers = clientsRes.data.clients || [];
        setClients(allUsers.filter(u => u.role === "client"));
      }
      await fetchCoaches();
      await fetchAssignments();
      await fetchExperts();
      await fetchPractices();
      await fetchExpertClientMappings();
      await fetchAllPracticeAssignments();
      setLoading(false);
    };
    fetchData();
  }, [token]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  const handleDeleteClient = async (client, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete client "${client.name}" (${client.email})?\n\nThis will permanently remove the client and ALL their data (profile, assessments, orders, invoices, sessions, etc.). This cannot be undone.`)) return;
    const res = await apiCall("DELETE", `/admin/client/${client.user_id}`, null, token);
    if (res.success) {
      toast.success(res.data.message);
      setClients(prev => prev.filter(c => c.user_id !== client.user_id));
      if (selectedClient?.user_id === client.user_id) {
        setSelectedClient(null);
        setClientDetail(null);
        setClientAnalytics(null);
      }
    } else toast.error(res.data?.detail || "Delete failed");
  };

  const handleEditClient = async (client, e) => {
    e.stopPropagation();
    const res = await apiCall("GET", `/admin/client/${client.user_id}`, null, token);
    if (res.success) {
      setEditingClient(client.user_id);
      setEditClientForm({
        name: res.data.name || "",
        email: res.data.email || "",
        mobile: res.data.mobile || "",
        country: res.data.country || "",
        client_region: res.data.client_region || "",
        client_id: res.data.client_id || "",
        subscription_status: res.data.subscription_status || "none",
        password: "",
      });
    }
  };

  const handleSaveClient = async () => {
    setEditClientSaving(true);
    const payload = { ...editClientForm };
    if (!payload.password) delete payload.password;
    const res = await apiCall("PUT", `/admin/client/${editingClient}`, payload, token);
    if (res.success) {
      toast.success(res.data.message);
      setEditingClient(null);
      // Refresh clients
      const r = await apiCall("GET", "/admin/dashboard", null, token);
      if (r.success) setClients(r.data.clients || []);
    } else toast.error(res.data?.detail || "Update failed");
    setEditClientSaving(false);
  };

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    const [detailRes, analyticsRes, notesRes, expertMapRes, recRes, assignRes] = await Promise.all([
      apiCall("GET", `/coach/client/${client.user_id}`, null, token),
      apiCall("GET", `/admin/client/${client.user_id}/analytics`, null, token),
      apiCall("GET", `/notes/client/${client.user_id}`, null, token),
      apiCall("GET", `/expert-client-map?client_id=${client.user_id}`, null, token),
      apiCall("GET", `/practices/recommend/${client.user_id}`, null, token),
      apiCall("GET", `/practice-assignments/client/${client.user_id}`, null, token),
    ]);
    if (detailRes.success) setClientDetail(detailRes.data);
    if (analyticsRes.success) setClientAnalytics(analyticsRes.data);
    if (notesRes.success) setClientNotes(notesRes.data.notes || []);
    if (expertMapRes.success) setClientExpertMappings(expertMapRes.data.mappings || []);
    if (recRes.success) setRecommendations(recRes.data);
    if (assignRes.success) {
      setClientPracticeAssignments(assignRes.data.assignments || []);
      setSelectedForAssignment([]);
    }
    setAdminEditingNoteId(null);
    setAdminShowNoteForm(false);
    setAdminNewNote("");
  };

  const handleAdminCreateNote = async () => {
    if (!adminNewNote.trim() || !selectedClient) return;
    const res = await apiCall("POST", `/notes/client/${selectedClient.user_id}`, { content: adminNewNote, category: adminNoteCategory }, token);
    if (res.success) {
      setClientNotes(prev => [res.data, ...prev]);
      setAdminNewNote("");
      setAdminNoteCategory("General");
      setAdminShowNoteForm(false);
      toast.success("Note added");
    } else { toast.error(res.error || "Failed to add note"); }
  };

  const handleAdminUpdateNote = async (noteId) => {
    if (!adminEditContent.trim()) return;
    const res = await apiCall("PUT", `/notes/${noteId}`, { content: adminEditContent, category: adminEditCategory }, token);
    if (res.success) {
      setClientNotes(prev => prev.map(n => n.note_id === noteId ? res.data : n));
      setAdminEditingNoteId(null);
      setAdminEditContent("");
      toast.success("Note updated");
    } else { toast.error(res.error || "Failed to update note"); }
  };

  const handleAdminDeleteNote = async (noteId) => {
    const res = await apiCall("DELETE", `/notes/${noteId}`, null, token);
    if (res.success) {
      setClientNotes(prev => prev.filter(n => n.note_id !== noteId));
      toast.success("Note deleted");
    } else { toast.error(res.error || "Failed to delete note"); }
  };

  const handleAssignCoach = async () => {
    if (!selectedClient || !assignCoachId) return;
    const res = await apiCall("POST", `/admin/assign_coach?client_id=${selectedClient.user_id}&coach_id=${assignCoachId}`, null, token);
    if (res.success) {
      toast.success(res.data.message);
      await fetchAssignments();
      await fetchCoaches();
      setAssignCoachId("");
    } else {
      toast.error(res.error);
    }
  };

  const handleUnassign = async (clientId, coachId) => {
    const res = await apiCall("DELETE", `/admin/unassign_coach?client_id=${clientId}&coach_id=${coachId}`, null, token);
    if (res.success) {
      toast.success("Coach removed");
      await fetchAssignments();
      await fetchCoaches();
    }
  };

  const getAssignedCoaches = (clientId) => {
    return assignments.filter(a => a.client_id === clientId);
  };

  const handleFetchRecommendations = async (clientId) => {
    const res = await apiCall("GET", `/practices/recommend/${clientId}`, null, token);
    if (res.success) setRecommendations(res.data);
  };

  const togglePracticeForAssignment = (pid) => {
    setSelectedForAssignment(prev => prev.includes(pid) ? prev.filter(id => id !== pid) : [...prev, pid]);
  };

  const handleAssignPractices = async () => {
    if (!selectedClient || selectedForAssignment.length === 0) return;
    const res = await apiCall("POST", `/practice-assignments/client/${selectedClient.user_id}`, {
      practice_ids: selectedForAssignment, notes: "Assigned by Admin"
    }, token);
    if (res.success) {
      toast.success(res.data.message);
      setSelectedForAssignment([]);
      handleFetchRecommendations(selectedClient.user_id);
    } else { toast.error(res.error || "Assignment failed"); }
  };

  const handleAssignExpertToSelectedClient = async (expertId) => {
    if (!selectedClient) return;
    const res = await apiCall("POST", "/expert-client-map", { expert_id: expertId, client_id: selectedClient.user_id }, token);
    if (res.success) {
      toast.success(res.data.message);
      const expertMapRes = await apiCall("GET", `/expert-client-map?client_id=${selectedClient.user_id}`, null, token);
      if (expertMapRes.success) setClientExpertMappings(expertMapRes.data.mappings || []);
      await fetchExpertClientMappings();
    } else { toast.error(res.error || "Assignment failed"); }
  };

  const handleRemoveExpertFromClient = async (mappingId) => {
    const res = await apiCall("DELETE", `/expert-client-map/${mappingId}`, null, token);
    if (res.success) {
      toast.success("Expert removed from client");
      setClientExpertMappings(prev => prev.filter(m => m.mapping_id !== mappingId));
      await fetchExpertClientMappings();
    } else { toast.error(res.error || "Failed to remove"); }
  };


  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.client_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-cosmic-void flex items-center justify-center">
        <div className="text-divine-gold animate-pulse font-heading text-2xl">Loading Admin Dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cosmic-void" data-testid="admin-dashboard">
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md border-b border-divine-gold/10 bg-cosmic-void/80">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-3xl text-divine-gold">ॐ</span>
            <span className="font-heading text-xl text-divine-gold tracking-wider hidden md:inline">SIWON HARMONY</span>
            <Badge className="bg-chakra-root text-white ml-2">ADMIN</Badge>
          </div>
          <div className="flex items-center gap-3">
            <DemoVideoButton className="text-sm" />
            <span className="text-chakra-crown/70 hidden md:inline">{user?.name}</span>
            <button onClick={logout} className="p-2 hover:text-divine-gold transition-colors text-chakra-crown/70" data-testid="logout-btn">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-3xl text-divine-gold mb-8">ADMIN CONTROL BOARD</h1>

          {/* Platform Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {[
              { label: "Active Clients", value: metrics?.active_clients || clients.length || 0, icon: Users, color: "text-divine-gold" },
              { label: "Premium", value: metrics?.premium_clients || 0, icon: Award, color: "text-chakra-heart" },
              { label: "Coaches", value: coaches.length || 0, icon: Users, color: "text-chakra-throat" },
              { label: "Experts", value: experts.length || 0, icon: Sparkles, color: "text-emerald-400" },
              { label: "Revenue", value: null, icon: IndianRupee, color: "text-divine-gold-light", custom: true },
              { label: "Assigned", value: assignments.length, icon: Link2, color: "text-divine-gold" },
            ].map((stat) => (
              <div key={stat.label} className="dashboard-widget" data-testid={`metric-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="flex items-center justify-between">
                  <span className="label-sacred text-xs">{stat.label}</span>
                  <stat.icon className={`w-4 h-4 ${stat.color}`} />
                </div>
                {stat.custom ? (
                  <div className="mt-2">
                    <p className="text-xl font-heading text-divine-gold-light">{formatCurrency(metrics?.revenue_inr || 0)}</p>
                    {(metrics?.revenue_usd || 0) > 0 && (
                      <p className="text-xs font-heading text-emerald-400 mt-0.5">${metrics.revenue_usd.toLocaleString()}</p>
                    )}
                  </div>
                ) : (
                  <p className={`text-xl font-heading mt-2 ${stat.color}`}>{stat.value}</p>
                )}
              </div>
            ))}
          </div>

          {/* Main Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-cosmic-deep/50 border border-divine-gold/10 p-1 rounded-xl">
              <TabsTrigger value="overview" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg px-6" data-testid="overview-tab">
                <BarChart3 className="w-4 h-4 mr-2" />Overview
              </TabsTrigger>
              <TabsTrigger value="clients" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg px-6" data-testid="clients-tab">
                <Users className="w-4 h-4 mr-2" />Clients ({clients.length})
              </TabsTrigger>
              <TabsTrigger value="coaches" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg px-6" data-testid="coaches-tab">
                <Award className="w-4 h-4 mr-2" />Coaches ({coaches.length})
              </TabsTrigger>
              <TabsTrigger value="analytics" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg px-6" data-testid="analytics-tab">
                <FlaskConical className="w-4 h-4 mr-2" />Analytics
              </TabsTrigger>
              <TabsTrigger value="experts" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg px-6" data-testid="experts-tab">
                <Sparkles className="w-4 h-4 mr-2" />Experts ({experts.length})
              </TabsTrigger>
              <TabsTrigger value="practices" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg px-6" data-testid="practices-tab">
                <BookOpen className="w-4 h-4 mr-2" />Practices ({practices.length})
              </TabsTrigger>
              <TabsTrigger value="reports" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg px-6" data-testid="reports-tab">
                <FileText className="w-4 h-4 mr-2" />Reports
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-divine-gold data-[state=active]:text-cosmic-void rounded-lg px-6" data-testid="settings-tab">
                <Target className="w-4 h-4 mr-2" />CTA & Settings
              </TabsTrigger>
            </TabsList>

            {/* ============ OVERVIEW TAB ============ */}
            <TabsContent value="overview" className="space-y-6">
              {/* Escalation Alerts */}
              <EscalationPanel token={token} />

              {/* Revenue by Gateway Account */}
              {metrics?.gateway_split && (
                <div className="sacred-card" data-testid="gateway-split-card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-xl text-divine-gold">
                      <IndianRupee className="w-5 h-5 inline mr-2" />REVENUE BY GATEWAY ACCOUNT
                    </h3>
                    <span className="text-[10px] tracking-wider text-chakra-crown/40">
                      Estimated Razorpay commission: 2% domestic · 3% international
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Domestic */}
                    <div className="p-5 rounded-xl border border-divine-gold/20 bg-divine-gold/5" data-testid="gateway-domestic">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-heading text-sm text-divine-gold tracking-wider">DOMESTIC (INR)</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-divine-gold/15 text-divine-gold">
                          {metrics.gateway_split.domestic.count} orders
                        </span>
                      </div>
                      <p className="text-2xl font-heading text-divine-gold-light mb-1">
                        {formatCurrency(metrics.gateway_split.domestic.inr || 0)}
                      </p>
                      {metrics.gateway_split.domestic.usd > 0 && (
                        <p className="text-xs text-chakra-crown/60 mb-1">
                          + ${metrics.gateway_split.domestic.usd.toLocaleString()} USD (routed domestic)
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs mt-3 pt-3 border-t border-divine-gold/10">
                        <span className="text-chakra-crown/50">Est. commission</span>
                        <span className="text-amber-400">
                          -{formatCurrency(metrics.est_commission?.domestic_inr || 0)}
                          {metrics.est_commission?.domestic_usd > 0 && ` / -$${metrics.est_commission.domestic_usd}`}
                        </span>
                      </div>
                    </div>

                    {/* International */}
                    <div className="p-5 rounded-xl border border-emerald-500/20 bg-emerald-500/5" data-testid="gateway-international">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-heading text-sm text-emerald-400 tracking-wider">INTERNATIONAL (USD)</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                          {metrics.gateway_split.international.count} orders
                        </span>
                      </div>
                      <p className="text-2xl font-heading text-emerald-400 mb-1">
                        ${(metrics.gateway_split.international.usd || 0).toLocaleString()}
                      </p>
                      {metrics.gateway_split.international.inr > 0 && (
                        <p className="text-xs text-chakra-crown/60 mb-1">
                          + {formatCurrency(metrics.gateway_split.international.inr)} INR (routed international)
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs mt-3 pt-3 border-t border-emerald-500/10">
                        <span className="text-chakra-crown/50">Est. commission</span>
                        <span className="text-amber-400">
                          {metrics.est_commission?.international_usd > 0 ? `-$${metrics.est_commission.international_usd}` : "—"}
                          {metrics.est_commission?.international_inr > 0 && ` / -${formatCurrency(metrics.est_commission.international_inr)}`}
                        </span>
                      </div>
                    </div>
                  </div>
                  {metrics.gateway_split.domestic.count + metrics.gateway_split.international.count === 0 && (
                    <p className="text-chakra-crown/40 text-xs text-center mt-4">
                      No paid orders yet. Cards will populate as clients make payments.
                    </p>
                  )}
                  {metrics.gateway_split.international.count === 0 && metrics.gateway_split.domestic.count > 0 && (
                    <p className="text-chakra-crown/40 text-[11px] mt-3 pt-3 border-t border-divine-gold/10">
                      <span className="text-emerald-400/80">Note:</span> Your current Razorpay account handles both INR and USD automatically — commissions (≈2% INR / 3% USD) are deducted at Razorpay's end. Only configure <code className="text-emerald-400/80 bg-emerald-500/10 px-1 rounded">RAZORPAY_KEY_ID_USD</code> in <code className="text-divine-gold/70 bg-divine-gold/10 px-1 rounded">backend/.env</code> if you open a <strong>separate</strong> international merchant account.
                    </p>
                  )}
                </div>
              )}

              {/* Stuck Assessments — silent-submit recovery */}
              {stuckAssessments.length > 0 && (
                <div className="sacred-card border-amber-500/30" data-testid="stuck-assessments-card">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-xl text-amber-400 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />STUCK ASSESSMENTS
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 ml-1">{stuckAssessments.length}</span>
                    </h3>
                    <span className="text-[10px] tracking-wider text-chakra-crown/40">All sections complete · Scores not generated</span>
                  </div>
                  <p className="text-chakra-crown/60 text-xs mb-3">
                    These clients finished all 4 required sections but their 12D results were not computed. Click <strong>Recompute</strong> to rebuild scores from saved answers — no data re-entry required.
                  </p>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {stuckAssessments.map((c) => (
                      <div key={c.user_id} className="flex items-center justify-between p-3 rounded-lg bg-cosmic-light/5 border border-amber-500/15" data-testid={`stuck-row-${c.user_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-heading text-sm text-divine-gold truncate">
                            {c.name}
                            {c.client_id && <span className="text-chakra-crown/40 ml-2 text-xs font-normal">{c.client_id}</span>}
                          </p>
                          <p className="text-chakra-crown/50 text-xs truncate">{c.email}</p>
                          {c.last_saved_at && (
                            <p className="text-chakra-crown/30 text-[10px] mt-0.5">
                              Last save: {new Date(c.last_saved_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          disabled={recomputing === c.user_id}
                          onClick={async () => {
                            if (!window.confirm(`Recompute 12D scores for ${c.name}?`)) return;
                            setRecomputing(c.user_id);
                            const r = await apiCall("POST", `/admin/recompute-profiling/${c.user_id}`, null, token);
                            if (r.success) {
                              toast.success(`Recomputed scores for ${c.name}`);
                              setStuckAssessments(prev => prev.filter(x => x.user_id !== c.user_id));
                            } else {
                              toast.error(r.error?.detail || r.error || "Recompute failed");
                            }
                            setRecomputing(null);
                          }}
                          className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 border border-amber-500/30 ml-3 shrink-0"
                          data-testid={`recompute-btn-${c.user_id}`}
                        >
                          {recomputing === c.user_id ? "..." : "Recompute"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="sacred-card">
                  <h3 className="font-heading text-xl text-divine-gold mb-6">
                    <BarChart3 className="w-5 h-5 inline mr-2" />TOP TRIGGERS DISTRIBUTION
                  </h3>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={trends?.top_triggers?.slice(0, 8) || []} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#ffd700" strokeOpacity={0.1} />
                        <XAxis type="number" stroke="#e0c3fc" />
                        <YAxis dataKey="_id" type="category" stroke="#e0c3fc" width={120} fontSize={12} />
                        <Tooltip contentStyle={{ backgroundColor: '#240046', border: '1px solid rgba(255,215,0,0.2)', borderRadius: '8px' }} />
                        <Bar dataKey="count" fill="#ffd700">
                          {(trends?.top_triggers || []).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={index % 2 === 0 ? '#ffd700' : '#e0c3fc'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="sacred-card space-y-5">
                  <h3 className="font-heading text-xl text-divine-gold">
                    <PieChartIcon className="w-5 h-5 inline mr-2" />TREND ANALYSIS
                    <span className="text-[10px] text-emerald-400 ml-2 font-normal tracking-wider">LIVE</span>
                  </h3>

                  {/* Assessment Completion Rates */}
                  {trends?.completion_rates && (
                    <div data-testid="trend-completion-rates">
                      <p className="text-[10px] font-heading text-chakra-crown/50 tracking-wider mb-2">ASSESSMENT COMPLETION ({trends.completion_rates.total_clients} clients)</p>
                      <div className="space-y-1.5">
                        {trends.completion_rates.assessments?.map((a, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-chakra-crown/70 text-xs w-[120px] truncate">{a.name}</span>
                            <div className="flex-1 h-2 rounded-full bg-cosmic-light/10 overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.min(a.pct, 100)}%`, backgroundColor: a.pct > 50 ? '#10b981' : a.pct > 20 ? '#f59e0b' : '#ef4444' }} />
                            </div>
                            <span className="text-[10px] text-chakra-crown/50 w-[48px] text-right">{a.completed}/{trends.completion_rates.total_clients}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Vikara Averages */}
                  {trends?.vikara_trends?.averages && (
                    <div data-testid="trend-vikara">
                      <p className="text-[10px] font-heading text-chakra-crown/50 tracking-wider mb-2">VIKARA AVERAGES ({trends.vikara_trends.total_respondents} respondents)</p>
                      <div className="grid grid-cols-3 gap-2">
                        {trends.vikara_trends.averages.map((v, i) => (
                          <div key={i} className="rounded-lg bg-cosmic-light/5 border border-orange-500/10 p-2 text-center">
                            <p className="text-orange-400/70 text-[10px] font-heading">{v.vikara}</p>
                            <p className="text-orange-300 text-lg font-heading">{v.avg_score}<span className="text-[10px] text-orange-400/40">/10</span></p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Guna Distribution */}
                  {trends?.guna_trends?.averages && (
                    <div data-testid="trend-guna">
                      <p className="text-[10px] font-heading text-chakra-crown/50 tracking-wider mb-2">GUNA DISTRIBUTION ({trends.guna_trends.total_respondents} respondents)</p>
                      <div className="flex gap-2">
                        {trends.guna_trends.averages.map((g, i) => {
                          const colors = ['#10b981', '#f59e0b', '#ef4444'];
                          return (
                            <div key={i} className="flex-1 rounded-lg p-2 text-center" style={{ backgroundColor: `${colors[i]}15`, border: `1px solid ${colors[i]}22` }}>
                              <p className="text-[10px] font-heading" style={{ color: `${colors[i]}99` }}>{g.guna}</p>
                              <p className="text-lg font-heading" style={{ color: colors[i] }}>{g.avg_score}<span className="text-[10px] opacity-40">/10</span></p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Kosha Averages */}
                  {trends?.kosha_trends?.averages && (
                    <div data-testid="trend-kosha">
                      <p className="text-[10px] font-heading text-chakra-crown/50 tracking-wider mb-2">KOSHA LAYER HEALTH ({trends.kosha_trends.total_respondents} respondents)</p>
                      <div className="space-y-1.5">
                        {trends.kosha_trends.averages.map((k, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-purple-300/70 text-xs w-[100px]">{k.kosha}</span>
                            <div className="flex-1 h-2.5 rounded-full bg-cosmic-light/10 overflow-hidden">
                              <div className="h-full rounded-full bg-purple-400 transition-all duration-700" style={{ width: `${(k.avg_score / 5) * 100}%` }} />
                            </div>
                            <span className="text-purple-300 text-xs font-heading w-[40px] text-right">{k.avg_score}/5</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tatkalik Top Triggers */}
                  {trends?.tatkalik_trends?.length > 0 && (
                    <div data-testid="trend-tatkalik">
                      <p className="text-[10px] font-heading text-chakra-crown/50 tracking-wider mb-2">TOP CONTEMPORARY TRIGGERS</p>
                      <div className="flex flex-wrap gap-1.5">
                        {trends.tatkalik_trends.map((t, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 bg-yellow-500/10 border border-yellow-500/20 text-yellow-300/80">
                            {t.label} <span className="text-yellow-500/50 text-[10px]">x{t.count}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Issue Diagnosis Trends */}
                  {trends?.issue_trends?.length > 0 && (
                    <div data-testid="trend-issues">
                      <p className="text-[10px] font-heading text-chakra-crown/50 tracking-wider mb-2">TOP REPORTED PAIN POINTS</p>
                      <div className="space-y-1.5">
                        {trends.issue_trends.slice(0, 5).map((t, i) => (
                          <div key={i} className="flex items-center justify-between p-2 rounded-lg bg-cosmic-light/5 border border-red-500/10">
                            <span className="text-red-300/70 text-xs flex-1 truncate mr-2">{t.label}</span>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-[10px] border-chakra-crown/20 text-chakra-crown/50">{t.module}</Badge>
                              <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400">{t.count}x</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Empty state */}
                  {!trends?.completion_rates && !trends?.vikara_trends?.averages && !trends?.guna_trends?.averages && (
                    <p className="text-chakra-crown/40 text-center py-6 text-sm">No assessment data yet. Trends will appear as clients complete assessments.</p>
                  )}
                </div>
              </div>

            </TabsContent>

            {/* ============ CLIENTS TAB ============ */}
            <TabsContent value="clients" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1">
                  <div className="sacred-card">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-heading text-lg text-divine-gold"><Users className="w-5 h-5 inline mr-2" />All Clients</h3>
                      <span className="text-chakra-crown/60 text-sm">{clients.length} total</span>
                    </div>
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-divine-gold/40" />
                      <Input placeholder="Search clients..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" data-testid="client-search" />
                    </div>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {filteredClients.map((client) => (
                        <div key={client.user_id} role="button" tabIndex={0}
                          onClick={() => handleSelectClient(client)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleSelectClient(client); } }}
                          className={`w-full p-3 rounded-xl text-left transition-all cursor-pointer ${
                            selectedClient?.user_id === client.user_id
                              ? "bg-divine-gold/20 border border-divine-gold"
                              : "bg-cosmic-light/10 border border-transparent hover:border-divine-gold/30"
                          }`} data-testid={`client-${client.user_id}`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-divine-gold font-medium">{client.name}</p>
                              <p className="text-chakra-crown/60 text-sm">{client.email}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={(e) => handleEditClient(client, e)}
                                className="p-1.5 rounded-lg text-divine-gold/30 hover:text-divine-gold hover:bg-divine-gold/10 transition-all"
                                title="Edit client" data-testid={`edit-client-${client.user_id}`}>
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={(e) => handleDeleteClient(client, e)}
                                className="p-1.5 rounded-lg text-red-400/30 hover:text-red-400 hover:bg-red-500/10 transition-all"
                                title="Delete client" data-testid={`delete-client-${client.user_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                              <ChevronRight className={`w-4 h-4 ${selectedClient?.user_id === client.user_id ? "text-divine-gold" : "text-chakra-crown/40"}`} />
                            </div>
                          </div>
                          <div className="mt-2 flex gap-2 flex-wrap">
                            <Badge variant="outline" className={`text-xs ${client.subscription_status === "premium" ? "border-divine-gold/50 text-divine-gold" : "border-chakra-crown/30 text-chakra-crown/70"}`}>
                              {client.subscription_status || "free"}
                            </Badge>
                            {client.profiling_complete && <Badge variant="outline" className="text-xs border-chakra-heart/30 text-chakra-heart">Profiled</Badge>}
                            {getAssignedCoaches(client.user_id).length > 0 && (
                              <Badge variant="outline" className="text-xs border-sky-400/30 text-sky-400">
                                Coach: {getAssignedCoaches(client.user_id).map(a => a.coach_name).join(", ")}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                      {filteredClients.length === 0 && <p className="text-center text-chakra-crown/50 py-8">No clients found</p>}
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-2">
                  {selectedClient && clientDetail ? (
                    <div className="space-y-4">
                      <div className="sacred-card">
                        <div className="flex items-start justify-between">
                          <div>
                            <h2 className="font-heading text-2xl text-divine-gold">{clientDetail.client?.name}</h2>
                            <p className="text-chakra-crown/60">{clientDetail.client?.email}</p>
                            <p className="text-divine-gold/70 font-mono text-sm mt-1">{clientDetail.client?.client_id}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Button variant="outline" size="sm" onClick={() => setShowProfileDialog(true)}
                              className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10" data-testid="view-profile-btn">
                              <User className="w-4 h-4 mr-2" />Profile
                            </Button>
                            <Button variant="outline" size="sm"
                              onClick={() => navigate(`/dashboard?view_as=${selectedClient.user_id}&from_tab=clients`)}
                              className="border-divine-gold/30 text-divine-gold hover:bg-divine-gold/10" data-testid="view-dashboard-btn">
                              <Eye className="w-4 h-4 mr-2" />View Dashboard
                            </Button>
                            <Badge className={clientDetail.client?.subscription_status === "premium" ? "bg-divine-gold text-cosmic-void" : "bg-cosmic-light text-chakra-crown"}>
                              {clientDetail.client?.subscription_status?.toUpperCase() || "FREE"}
                            </Badge>
                            <select
                              value={clientDetail.client?.client_region || "domestic"}
                              onChange={async (e) => {
                                const r = await apiCall("PUT", `/admin/client-region/${selectedClient.user_id}`, { client_region: e.target.value }, token);
                                if (r.success) { toast.success(`Region set to ${e.target.value}`); const res = await apiCall("GET", `/coach/client/${selectedClient.user_id}`, null, token); if (res.success) setClientDetail(res.data); }
                                else toast.error(r.error || "Failed to update");
                              }}
                              className="h-6 px-2 rounded text-[10px] bg-cosmic-deep border border-divine-gold/20 text-divine-gold cursor-pointer"
                              data-testid="client-region-select"
                            >
                              <option value="domestic">Domestic</option>
                              <option value="international">International</option>
                            </select>
                          </div>
                        </div>

                        {/* Coach Assignment - Multiple */}
                        <div className="mt-4 p-3 rounded-lg bg-cosmic-light/10 border border-divine-gold/10">
                          <span className="label-sacred text-xs block mb-2">ASSIGNED COACHES:</span>
                          <div className="flex flex-wrap items-center gap-2">
                            {getAssignedCoaches(selectedClient.user_id).map(a => (
                              <div key={a.coach_id} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-sky-500/10 border border-sky-500/20">
                                <Badge className="bg-sky-500/20 text-sky-400 text-xs">{a.coach_name}</Badge>
                                <button onClick={() => handleUnassign(selectedClient.user_id, a.coach_id)}
                                  className="text-chakra-crown/40 hover:text-red-400 transition-colors" data-testid={`remove-coach-${a.coach_id}`}>
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                            <div className="flex items-center gap-2">
                              <Select value={assignCoachId} onValueChange={setAssignCoachId}>
                                <SelectTrigger className="w-44 h-8 bg-cosmic-deep border-divine-gold/20 text-chakra-crown text-sm">
                                  <SelectValue placeholder="+ Add coach..." />
                                </SelectTrigger>
                                <SelectContent className="bg-cosmic-deep border-divine-gold/20">
                                  {coaches
                                    .filter(c => !getAssignedCoaches(selectedClient.user_id).some(a => a.coach_id === c.user_id))
                                    .map((c) => (
                                      <SelectItem key={c.user_id} value={c.user_id} className="text-chakra-crown">{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                              <Button size="sm" onClick={handleAssignCoach} className="bg-divine-gold text-cosmic-void h-8" disabled={!assignCoachId}
                                data-testid="assign-coach-btn">
                                <Link2 className="w-3 h-3 mr-1" />Assign
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Coach Assessment Matrix Activation */}
                        <div className="mt-3 p-3 rounded-lg bg-cosmic-light/10 border border-divine-gold/10">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="label-sacred text-xs">COACH ASSESSMENT MATRIX:</span>
                              <Badge className={clientDetail.client?.coach_assessment_active
                                ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                                : "bg-amber-500/20 text-amber-300 border-amber-500/40"
                              } data-testid="admin-matrix-status-badge">
                                {clientDetail.client?.coach_assessment_active ? "● ACTIVE" : "○ INACTIVE"}
                              </Badge>
                              <span className="text-chakra-crown/50 text-[11px]">
                                {clientDetail.client?.coach_assessment_active
                                  ? "Client can fill the 30-element matrix."
                                  : "Client sees the matrix in preview-only mode."}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid="admin-toggle-coach-assessment-btn"
                              onClick={async () => {
                                const isActive = !!clientDetail.client?.coach_assessment_active;
                                const action = isActive ? "deactivate" : "activate";
                                const verb = isActive ? "Deactivate" : "Activate";
                                if (!window.confirm(`${verb} Coach Assessment Matrix for ${clientDetail.client?.name}?\n\n${isActive ? "They will lose the ability to save responses (existing data is preserved)." : "They will be able to begin filling the 30-element matrix."}`)) return;
                                const r = await apiCall("POST", `/coach-assessment-orders/admin/${selectedClient.user_id}/${action}`, { reason: "admin toggle" }, token);
                                if (r.success) {
                                  toast.success(`Matrix ${isActive ? "deactivated" : "activated"}`);
                                  const res = await apiCall("GET", `/coach/client/${selectedClient.user_id}`, null, token);
                                  if (res.success) setClientDetail(res.data);
                                } else {
                                  toast.error(r.error || `${verb} failed`);
                                }
                              }}
                              className={clientDetail.client?.coach_assessment_active
                                ? "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                                : "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                              }
                            >
                              {clientDetail.client?.coach_assessment_active ? "Deactivate" : "Activate"}
                            </Button>
                          </div>
                        </div>

                        {/* Expert Assignment - Multiple */}
                        <div className="mt-3 p-3 rounded-lg bg-cosmic-light/10 border border-emerald-500/10">
                          <span className="label-sacred text-xs block mb-2">ASSIGNED EXPERTS:</span>
                          <div className="flex flex-wrap items-center gap-2">
                            {clientExpertMappings.map(m => (
                              <div key={m.mapping_id} className="flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                                  <Sparkles className="w-3 h-3 mr-1" />{m.expert_name}
                                </Badge>
                                <button onClick={() => handleRemoveExpertFromClient(m.mapping_id)}
                                  className="text-chakra-crown/40 hover:text-red-400 transition-colors" data-testid={`remove-expert-${m.mapping_id}`}>
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                            <select className="p-1.5 rounded-lg bg-cosmic-deep border border-divine-gold/20 text-chakra-crown text-sm h-8"
                              data-testid="assign-expert-to-client-select"
                              onChange={(e) => { if (e.target.value) handleAssignExpertToSelectedClient(e.target.value); e.target.value = ""; }}>
                              <option value="">+ Add expert...</option>
                              {experts
                                .filter(ex => !clientExpertMappings.some(m => m.expert_id === ex.user_id))
                                .map(ex => <option key={ex.user_id} value={ex.user_id}>{ex.name}</option>)}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Module Selector */}
                      <div className="flex gap-3" data-testid="admin-module-selector">
                        {[
                          { id: "relationship", label: "Relationship Wisdom Coaching", icon: Heart, desc: "3A Journey" },
                          { id: "life", label: "Life Wisdom Coaching", icon: Sparkles, desc: "Kosha & Practices" },
                        ].map(m => (
                          <button key={m.id} onClick={() => setClientModule(m.id)}
                            className={`flex-1 p-3 rounded-xl border transition-all flex items-center gap-3 ${
                              clientModule === m.id
                                ? "bg-divine-gold/10 border-divine-gold text-divine-gold"
                                : "bg-cosmic-light/5 border-divine-gold/10 text-chakra-crown/60 hover:border-divine-gold/30"
                            }`}
                            data-testid={`admin-module-${m.id}`}
                          >
                            <m.icon className={`w-5 h-5 ${clientModule === m.id ? "text-divine-gold" : "text-chakra-crown/40"}`} />
                            <div className="text-left">
                              <span className="font-heading text-sm block">{m.label}</span>
                              <span className="text-xs opacity-60">{m.desc}</span>
                            </div>
                          </button>
                        ))}
                      </div>

                      {/* ======= RELATIONSHIP MODULE ======= */}
                      {clientModule === "relationship" && (
                        <>

                      {/* Scores & Triggers */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { label: "TRINITY GAP", value: `${clientDetail.scores?.trinity_gap?.toFixed(1) || 0}%`, color: "text-divine-gold" },
                          { label: "GUNA", value: clientDetail.scores?.guna?.dominant || "N/A", color: "text-chakra-crown" },
                          { label: "MASCULINE", value: `${(clientDetail.scores?.energy?.masculine * 100)?.toFixed(0) || 0}%`, color: "text-divine-gold" },
                          { label: "FEMININE", value: `${(clientDetail.scores?.energy?.feminine * 100)?.toFixed(0) || 0}%`, color: "text-chakra-crown" },
                        ].map((s) => (
                          <div key={s.label} className="dashboard-widget">
                            <span className="label-sacred">{s.label}</span>
                            <p className={`text-2xl font-heading mt-2 capitalize ${s.color}`}>{s.value}</p>
                          </div>
                        ))}
                      </div>

                      {/* Energy Polarity: Self-Assessment (Q8) */}
                      {(() => {
                        const q8 = clientDetail.raw_answers?.find(a => a.question_id === 'Q8');
                        const selfM = q8?.answer_options?.masculine;
                        const selfF = q8?.answer_options?.feminine;
                        if (!selfM && !selfF) return null;
                        return (
                          <div className="sacred-card" data-testid="admin-energy-self-assessment">
                            <h4 className="label-sacred text-xs mb-3">ENERGY POLARITY — SELF-ASSESSMENT (vs Pillar-based: M {(clientDetail.scores?.energy?.masculine * 100)?.toFixed(0) || 0}% / F {(clientDetail.scores?.energy?.feminine * 100)?.toFixed(0) || 0}%)</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <p className="text-sky-400 text-xs font-medium mb-2">MASCULINE QUALITIES</p>
                                {selfM && Object.entries(selfM).map(([k, v]) => (
                                  <div key={k} className="mb-1.5">
                                    <div className="flex justify-between text-xs"><span className="text-chakra-crown/60 capitalize">{k.replace(/_/g, ' ')}</span><span className="text-sky-400">{v}/5</span></div>
                                    <div className="h-1.5 bg-cosmic-deep rounded-full"><div className="h-full bg-sky-500 rounded-full" style={{ width: `${(v / 5) * 100}%` }} /></div>
                                  </div>
                                ))}
                              </div>
                              <div>
                                <p className="text-pink-400 text-xs font-medium mb-2">FEMININE QUALITIES</p>
                                {selfF && Object.entries(selfF).map(([k, v]) => (
                                  <div key={k} className="mb-1.5">
                                    <div className="flex justify-between text-xs"><span className="text-chakra-crown/60 capitalize">{k.replace(/_/g, ' ')}</span><span className="text-pink-400">{v}/5</span></div>
                                    <div className="h-1.5 bg-cosmic-deep rounded-full"><div className="h-full bg-pink-500 rounded-full" style={{ width: `${(v / 5) * 100}%` }} /></div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* Per-Client Profiling Download */}
                      <div className="flex items-center gap-3">
                        <span className="text-chakra-crown/60 text-sm">Download Profiling Report:</span>
                        <ClientProfilingDownload userId={selectedClient.user_id} clientName={selectedClient.name || ''} token={token} />
                      </div>

                      {/* Top Triggers */}
                      <div className="sacred-card">
                        <h3 className="font-heading text-lg text-divine-gold mb-4"><Eye className="w-5 h-5 inline mr-2" />TOP TRIGGERS</h3>
                        <div className="space-y-3">
                          {clientDetail.top_triggers?.map((trigger) => (
                            <div key={trigger.id} className="p-3 rounded-xl bg-cosmic-light/10 border border-divine-gold/10">
                              <div className="flex items-center justify-between">
                                <span className="text-divine-gold font-medium">{trigger.name}</span>
                                <div className="flex gap-2 text-xs">
                                  <span className="text-chakra-crown/60">{trigger.abhideya}</span>
                                  <span className="text-chakra-crown/40">{trigger.gita_verse}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                          {(!clientDetail.top_triggers || clientDetail.top_triggers.length === 0) && (
                            <p className="text-chakra-crown/50 text-center py-4">No triggers identified yet</p>
                          )}
                        </div>
                      </div>

                      {/* Relationship Kosha Quiz Results */}
                      <div className="sacred-card" data-testid="admin-rel-kosha-quiz">
                        <KoshaQuizResultsView token={token} clientId={selectedClient.user_id} quizType="relationship" />
                      </div>

                        </>
                      )}

                      {/* ======= LIFE MODULE ======= */}
                      {clientModule === "life" && (
                        <>
                      {/* Client Diagnostic Results */}
                      <div className="sacred-card" data-testid="admin-diagnostic-results">
                        <DiagnosticResultsView token={token} clientId={selectedClient.user_id} clientName={selectedClient.name} />
                      </div>

                      {/* Life Kosha Quiz Results */}
                      <div className="sacred-card" data-testid="admin-life-kosha-quiz">
                        <KoshaQuizResultsView token={token} clientId={selectedClient.user_id} quizType="life" />
                      </div>

                      {/* Assigned Practices */}
                      <div className="sacred-card">
                        <h3 className="font-heading text-lg text-divine-gold mb-3">
                          <Target className="w-5 h-5 inline mr-2" />ASSIGNED PRACTICES ({clientPracticeAssignments.length})
                        </h3>
                        <div className="space-y-2 max-h-[250px] overflow-y-auto">
                          {clientPracticeAssignments.map(a => (
                            <div key={a.assignment_id} className="p-2.5 rounded-lg bg-cosmic-light/5 border border-divine-gold/10 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {a.compliance_status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-chakra-crown/30" />}
                                <div>
                                  <span className="text-divine-gold text-sm">{a.practice_name}</span>
                                  <span className="text-chakra-crown/40 text-xs ml-2">{a.category} | {a.kosha_primary}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                          {clientPracticeAssignments.length === 0 && <p className="text-chakra-crown/40 text-center py-4 text-sm">No practices assigned</p>}
                        </div>
                      </div>

                      {/* Practice Recommendations */}
                      {recommendations?.recommendations_by_category && Object.keys(recommendations.recommendations_by_category).length > 0 && (
                        <div className="sacred-card">
                          <h3 className="font-heading text-lg text-divine-gold mb-3">PRACTICE RECOMMENDATIONS</h3>
                          <div className="space-y-3 max-h-[250px] overflow-y-auto">
                            {Object.entries(recommendations.recommendations_by_category).map(([category, practices]) => (
                              <div key={category} className="rounded-lg border border-divine-gold/15 overflow-hidden" data-testid={`admin-rec-category-${category.replace(/\s+/g, '-').toLowerCase()}`}>
                                <div className="flex items-center gap-2 p-2 bg-cosmic-light/10">
                                  <Target className="w-3.5 h-3.5 text-divine-gold/60" />
                                  <span className="font-heading text-xs text-divine-gold">{category.toUpperCase()}</span>
                                  <Badge className="bg-divine-gold/15 text-divine-gold/80 text-xs">{practices.length}</Badge>
                                </div>
                                <div className="p-2 space-y-1">
                                  {practices.map(p => (
                                    <div key={p.practice_id} className="p-2 rounded-lg bg-cosmic-light/5 border border-divine-gold/5 flex items-center justify-between">
                                      <div>
                                        <span className="text-divine-gold text-sm">{p.name}</span>
                                      </div>
                                      <div className="flex gap-1.5">
                                        <span className="text-emerald-400 text-xs">{p.kosha_primary}</span>
                                        <span className="text-chakra-crown/30 text-xs">{p.difficulty}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                        </>
                      )}

                      {/* Coach Notes Section (shared across both modules) */}
                      <div className="sacred-card" data-testid="admin-client-notes">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-heading text-lg text-divine-gold"><FileText className="w-5 h-5 inline mr-2" />SESSION NOTES</h3>
                          {!adminShowNoteForm && (
                            <Button size="sm" onClick={() => setAdminShowNoteForm(true)} className="bg-divine-gold text-cosmic-void hover:bg-divine-gold-light" data-testid="admin-add-note-btn">
                              <Plus className="w-4 h-4 mr-1" />Add Note
                            </Button>
                          )}
                        </div>
                        {adminShowNoteForm && (
                          <div className="mb-4 p-3 rounded-xl bg-cosmic-light/10 border border-divine-gold/20" data-testid="admin-new-note-form">
                            <div className="flex gap-2 mb-2 flex-wrap">
                              {NOTE_CATEGORIES.map((cat) => (
                                <button key={cat} onClick={() => setAdminNoteCategory(cat)}
                                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${adminNoteCategory === cat ? "bg-divine-gold text-cosmic-void" : "bg-cosmic-deep border border-divine-gold/20 text-chakra-crown/60 hover:border-divine-gold/40"}`}
                                  data-testid={`admin-category-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                                >{cat}</button>
                              ))}
                            </div>
                            <textarea value={adminNewNote} onChange={(e) => setAdminNewNote(e.target.value)} placeholder="Write session notes..."
                              className="w-full min-h-[80px] p-3 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown placeholder:text-chakra-crown/30 resize-y focus:outline-none focus:border-divine-gold/50"
                              data-testid="admin-note-textarea" />
                            <div className="flex gap-2 mt-2">
                              <Button size="sm" onClick={handleAdminCreateNote} disabled={!adminNewNote.trim()} className="bg-divine-gold text-cosmic-void" data-testid="admin-save-note-btn">
                                <Save className="w-4 h-4 mr-1" />Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => { setAdminShowNoteForm(false); setAdminNewNote(""); setAdminNoteCategory("General"); }} className="text-chakra-crown/60">
                                <X className="w-4 h-4 mr-1" />Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                        {/* Category Filter */}
                        <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="admin-note-category-filter">
                          {["All", ...NOTE_CATEGORIES].map((cat) => (
                            <button key={cat} onClick={() => setAdminFilterCategory(cat)}
                              className={`px-2.5 py-0.5 rounded-full text-xs font-medium transition-all ${adminFilterCategory === cat ? "bg-divine-gold/20 text-divine-gold border border-divine-gold/50" : "bg-cosmic-deep/50 border border-divine-gold/10 text-chakra-crown/50 hover:border-divine-gold/30"}`}
                              data-testid={`admin-filter-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                            >{cat}{cat !== "All" && ` (${clientNotes.filter(n => (n.category || "General") === cat).length})`}</button>
                          ))}
                        </div>
                        <div className="space-y-3 max-h-[300px] overflow-y-auto">
                          {clientNotes.filter(n => adminFilterCategory === "All" || (n.category || "General") === adminFilterCategory).map((note) => (
                            <div key={note.note_id} className="p-3 rounded-xl bg-cosmic-light/10 border border-divine-gold/10" data-testid={`admin-note-${note.note_id}`}>
                              {adminEditingNoteId === note.note_id ? (
                                <div>
                                  <div className="flex gap-1.5 mb-2 flex-wrap">
                                    {NOTE_CATEGORIES.map((cat) => (
                                      <button key={cat} onClick={() => setAdminEditCategory(cat)}
                                        className={`px-2 py-0.5 rounded-full text-xs transition-all ${adminEditCategory === cat ? "bg-divine-gold text-cosmic-void" : "bg-cosmic-deep border border-divine-gold/20 text-chakra-crown/60"}`}
                                      >{cat}</button>
                                    ))}
                                  </div>
                                  <textarea value={adminEditContent} onChange={(e) => setAdminEditContent(e.target.value)}
                                    className="w-full min-h-[60px] p-2 rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown resize-y focus:outline-none focus:border-divine-gold/50"
                                    data-testid="admin-edit-note-textarea" />
                                  <div className="flex gap-2 mt-2">
                                    <Button size="sm" onClick={() => handleAdminUpdateNote(note.note_id)} disabled={!adminEditContent.trim()} className="bg-divine-gold text-cosmic-void" data-testid="admin-save-edit-btn">
                                      <Save className="w-3 h-3 mr-1" />Save
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => setAdminEditingNoteId(null)} className="text-chakra-crown/60">Cancel</Button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-divine-gold font-medium text-sm">{note.author_name}</span>
                                      <Badge variant="outline" className="text-xs border-divine-gold/20 text-divine-gold/60 capitalize">{note.author_role}</Badge>
                                      <Badge className="text-xs bg-cosmic-deep border border-divine-gold/20 text-chakra-crown/70">{note.category || "General"}</Badge>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {note.author_id === user?.user_id && (
                                        <button onClick={() => { setAdminEditingNoteId(note.note_id); setAdminEditContent(note.content); setAdminEditCategory(note.category || "General"); }} className="p-1 rounded hover:bg-divine-gold/10 text-chakra-crown/50 hover:text-divine-gold transition-colors" data-testid={`admin-edit-note-${note.note_id}`}>
                                          <Edit className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      <button onClick={() => handleAdminDeleteNote(note.note_id)} className="p-1 rounded hover:bg-chakra-root/10 text-chakra-crown/50 hover:text-chakra-root transition-colors" data-testid={`admin-delete-note-${note.note_id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                  <p className="text-chakra-crown text-sm whitespace-pre-wrap">{note.content}</p>
                                  <p className="text-chakra-crown/40 text-xs mt-1">
                                    {new Date(note.created_at).toLocaleString()}
                                    {note.updated_at !== note.created_at && " (edited)"}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                          {clientNotes.filter(n => adminFilterCategory === "All" || (n.category || "General") === adminFilterCategory).length === 0 && <p className="text-chakra-crown/50 text-center py-4 text-sm">{adminFilterCategory === "All" ? "No session notes yet" : `No ${adminFilterCategory} notes`}</p>}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="sacred-card flex items-center justify-center h-96">
                      <p className="text-chakra-crown/50">Select a client to view details</p>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* ============ COACHES TAB ============ */}
            <TabsContent value="coaches" className="space-y-6">
              <Suspense fallback={<div className="p-8 text-center text-divine-gold/50 animate-pulse">Loading...</div>}>
                <AdminCoachesPanel token={token} coaches={coaches} clients={clients} assignments={assignments} fetchCoaches={fetchCoaches} fetchAssignments={fetchAssignments} />
              </Suspense>
            </TabsContent>
            {/* ============ ANALYTICS REVIEW TAB ============ */}
            <TabsContent value="analytics" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Client Selector */}
                <div className="lg:col-span-1">
                  <div className="sacred-card">
                    <h3 className="font-heading text-lg text-divine-gold mb-4"><FlaskConical className="w-5 h-5 inline mr-2" />Select Client</h3>
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-divine-gold/40" />
                      <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown" />
                    </div>
                    <div className="space-y-2 max-h-[600px] overflow-y-auto">
                      {filteredClients.filter(c => c.profiling_complete).map((client) => (
                        <button key={client.user_id} onClick={() => handleSelectClient(client)}
                          className={`w-full p-3 rounded-xl text-left transition-all ${
                            selectedClient?.user_id === client.user_id
                              ? "bg-divine-gold/20 border border-divine-gold" : "bg-cosmic-light/10 border border-transparent hover:border-divine-gold/30"
                          }`}>
                          <p className="text-divine-gold font-medium text-sm">{client.name}</p>
                          <p className="text-chakra-crown/50 text-xs">{client.email}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Analytics Detail */}
                <div className="lg:col-span-2">
                  {clientAnalytics && !clientAnalytics.error ? (
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="sacred-card">
                        <div className="flex items-center justify-between">
                          <h2 className="font-heading text-xl text-divine-gold">METHODOLOGY REVIEW: {selectedClient?.name}</h2>
                          <Badge className="bg-sky-500/20 text-sky-400">{clientAnalytics.assigned_coach}</Badge>
                        </div>
                        <p className="text-chakra-crown/50 text-sm mt-1">Calculated: {clientAnalytics.calculated_at ? new Date(clientAnalytics.calculated_at).toLocaleString() : 'N/A'}</p>
                      </div>

                      {/* Trinity Gap */}
                      <div className="sacred-card" data-testid="analytics-trinity">
                        <h3 className="font-heading text-lg text-divine-gold mb-2">TRINITY GAP (Gita 14.8)</h3>
                        <p className="text-chakra-crown/60 text-sm mb-3">{clientAnalytics.trinity_gap?.concept}</p>
                        <div className="p-3 rounded-lg bg-cosmic-light/10 font-mono text-xs text-divine-gold-light mb-3 whitespace-pre-wrap">
                          {clientAnalytics.trinity_gap?.formula}
                        </div>
                        <div className="grid grid-cols-4 gap-3 mb-4">
                          {[
                            { label: "Rajas Distortion", value: `${clientAnalytics.trinity_gap?.breakdown?.rajas_distortion?.toFixed(2) || 0}%` },
                            { label: "Tamas Distortion", value: `${clientAnalytics.trinity_gap?.breakdown?.tamas_distortion?.toFixed(2) || 0}%` },
                            { label: "Q9 (Vision)", value: `${clientAnalytics.trinity_gap?.breakdown?.q9_contribution?.toFixed(2) || 0}%` },
                            { label: "TRINITY GAP", value: `${clientAnalytics.trinity_gap?.value?.toFixed(1) || 0}%` },
                          ].map(s => (
                            <div key={s.label} className="text-center p-2 rounded bg-cosmic-deep">
                              <p className="text-chakra-crown/50 text-xs">{s.label}</p>
                              <p className="text-divine-gold font-heading text-lg">{s.value}</p>
                            </div>
                          ))}
                        </div>
                        {clientAnalytics.trinity_gap?.breakdown?.rajas_detail && (
                          <div className="grid grid-cols-2 gap-3">
                            <div className="p-3 rounded-lg bg-cosmic-deep">
                              <h4 className="text-chakra-root font-medium text-sm mb-2">RAJAS (55%)</h4>
                              {Object.entries(clientAnalytics.trinity_gap.breakdown.rajas_detail).map(([qid, d]) => (
                                <div key={qid} className="flex justify-between text-xs py-0.5">
                                  <span className="text-chakra-crown/60">{qid} ({(d.weight * 100).toFixed(0)}%)</span>
                                  <span className="text-chakra-crown">{d.raw}/10 = <span className="text-divine-gold">{d.weighted?.toFixed(2)}%</span></span>
                                </div>
                              ))}
                            </div>
                            <div className="p-3 rounded-lg bg-cosmic-deep">
                              <h4 className="text-indigo-400 font-medium text-sm mb-2">TAMAS (42%)</h4>
                              {Object.entries(clientAnalytics.trinity_gap.breakdown.tamas_detail || {}).map(([qid, d]) => (
                                <div key={qid} className="flex justify-between text-xs py-0.5">
                                  <span className="text-chakra-crown/60">{qid} ({(d.weight * 100).toFixed(0)}%)</span>
                                  <span className="text-chakra-crown">{d.raw}/10 = <span className="text-divine-gold">{d.weighted?.toFixed(2)}%</span></span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Energy Polarity */}
                      <div className="sacred-card" data-testid="analytics-energy">
                        <h3 className="font-heading text-lg text-divine-gold mb-2">ENERGY POLARITY</h3>
                        <p className="text-chakra-crown/60 text-sm mb-3">{clientAnalytics.energy_polarity?.concept}</p>
                        <div className="p-3 rounded-lg bg-cosmic-light/10 font-mono text-sm text-divine-gold-light mb-3">
                          {clientAnalytics.energy_polarity?.formula}
                        </div>
                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div className="text-center p-3 rounded bg-cosmic-deep">
                            <p className="text-chakra-crown/50 text-xs">Masculine</p>
                            <p className="text-divine-gold font-heading text-2xl">{(clientAnalytics.energy_polarity?.masculine * 100)?.toFixed(1)}%</p>
                          </div>
                          <div className="text-center p-3 rounded bg-cosmic-deep">
                            <p className="text-chakra-crown/50 text-xs">Feminine</p>
                            <p className="text-chakra-crown font-heading text-2xl">{(clientAnalytics.energy_polarity?.feminine * 100)?.toFixed(1)}%</p>
                          </div>
                          <div className="text-center p-3 rounded bg-cosmic-deep">
                            <p className="text-chakra-crown/50 text-xs">Harmony Potential</p>
                            <p className="text-chakra-heart font-heading text-2xl">{(clientAnalytics.energy_polarity?.harmony_potential * 100)?.toFixed(1)}%</p>
                          </div>
                        </div>
                        {/* Self-Assessment (Q8) */}
                        {(() => {
                          const q8 = clientDetail?.raw_answers?.find(a => a.question_id === 'Q8');
                          const selfM = q8?.answer_options?.masculine;
                          const selfF = q8?.answer_options?.feminine;
                          if (!selfM && !selfF) return null;
                          return (
                            <div className="mt-3 pt-3 border-t border-divine-gold/10" data-testid="analytics-energy-self">
                              <h4 className="label-sacred text-xs mb-3">SELF-ASSESSMENT COMPARISON</h4>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sky-400 text-xs font-medium mb-2">MASCULINE QUALITIES</p>
                                  {Object.entries(selfM).map(([k, v]) => (
                                    <div key={k} className="mb-1.5">
                                      <div className="flex justify-between text-xs"><span className="text-chakra-crown/60 capitalize">{k.replace(/_/g, ' ')}</span><span className="text-sky-400">{v}/5</span></div>
                                      <div className="h-1.5 bg-cosmic-deep rounded-full"><div className="h-full bg-sky-500 rounded-full" style={{ width: `${(v / 5) * 100}%` }} /></div>
                                    </div>
                                  ))}
                                </div>
                                <div>
                                  <p className="text-pink-400 text-xs font-medium mb-2">FEMININE QUALITIES</p>
                                  {Object.entries(selfF).map(([k, v]) => (
                                    <div key={k} className="mb-1.5">
                                      <div className="flex justify-between text-xs"><span className="text-chakra-crown/60 capitalize">{k.replace(/_/g, ' ')}</span><span className="text-pink-400">{v}/5</span></div>
                                      <div className="h-1.5 bg-cosmic-deep rounded-full"><div className="h-full bg-pink-500 rounded-full" style={{ width: `${(v / 5) * 100}%` }} /></div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Guna Distribution */}
                      <div className="sacred-card" data-testid="analytics-guna">
                        <h3 className="font-heading text-lg text-divine-gold mb-2">GUNA DISTRIBUTION</h3>
                        <p className="text-chakra-crown/60 text-sm mb-3">{clientAnalytics.guna_distribution?.concept}</p>
                        <div className="p-3 rounded-lg bg-cosmic-light/10 font-mono text-sm text-divine-gold-light mb-3">
                          {clientAnalytics.guna_distribution?.formula}
                        </div>
                        <div className="grid grid-cols-4 gap-3 mb-3">
                          {[
                            { label: "Rajas", value: `${clientAnalytics.guna_distribution?.rajas?.toFixed(1)}%`, color: "text-chakra-root" },
                            { label: "Tamas", value: `${clientAnalytics.guna_distribution?.tamas?.toFixed(1)}%`, color: "text-purple-400" },
                            { label: "Sattva", value: `${clientAnalytics.guna_distribution?.sattva?.toFixed(1)}%`, color: "text-chakra-heart" },
                            { label: "Dominant", value: clientAnalytics.guna_distribution?.dominant, color: "text-divine-gold" },
                          ].map(s => (
                            <div key={s.label} className="text-center p-2 rounded bg-cosmic-deep">
                              <p className="text-chakra-crown/50 text-xs">{s.label}</p>
                              <p className={`font-heading text-lg capitalize ${s.color}`}>{s.value}</p>
                            </div>
                          ))}
                        </div>
                        {/* Guna bar */}
                        <div className="flex h-4 rounded-full overflow-hidden">
                          <div className="bg-chakra-root" style={{ width: `${clientAnalytics.guna_distribution?.rajas || 0}%` }} />
                          <div className="bg-purple-500" style={{ width: `${clientAnalytics.guna_distribution?.tamas || 0}%` }} />
                          <div className="bg-chakra-heart" style={{ width: `${clientAnalytics.guna_distribution?.sattva || 0}%` }} />
                        </div>
                        <div className="flex justify-between text-xs text-chakra-crown/40 mt-1">
                          <span>Rajas</span><span>Tamas</span><span>Sattva</span>
                        </div>
                      </div>

                      {/* Vikara Intensities */}
                      <div className="sacred-card" data-testid="analytics-vikara">
                        <h3 className="font-heading text-lg text-divine-gold mb-2">VIKARA INTENSITIES</h3>
                        <p className="text-chakra-crown/60 text-sm mb-3">{clientAnalytics.vikara_intensities?.concept}</p>
                        <div className="p-3 rounded-lg bg-cosmic-light/10 font-mono text-sm text-divine-gold-light mb-3">
                          {clientAnalytics.vikara_intensities?.formula}
                        </div>
                        <div className="space-y-3">
                          {[
                            { name: "Kama (Desire)", key: "kama", color: "bg-chakra-root" },
                            { name: "Krodha (Anger)", key: "krodha", color: "bg-red-500" },
                            { name: "Lobha (Greed)", key: "lobha", color: "bg-amber-500" },
                            { name: "Moha (Delusion)", key: "moha", color: "bg-purple-500" },
                            { name: "Mada (Pride)", key: "mada", color: "bg-orange-500" },
                            { name: "Matsarya (Envy)", key: "matsarya", color: "bg-emerald-500" },
                          ].map((v) => (
                            <div key={v.key}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-chakra-crown/70 text-sm">{v.name}</span>
                                <span className="text-divine-gold text-sm font-heading">
                                  {clientAnalytics.vikara_intensities?.values?.[v.key]?.toFixed(2) || 0}/10
                                </span>
                              </div>
                              <Progress value={(clientAnalytics.vikara_intensities?.values?.[v.key] || 0) * 10} className={`h-2 bg-cosmic-deep [&>div]:${v.color}`} />
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Trigger Board Summary */}
                      <div className="sacred-card">
                        <h3 className="font-heading text-lg text-divine-gold mb-3">TRIGGER BOARD SUMMARY</h3>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                          {clientAnalytics.trigger_board?.map((p) => (
                            <div key={p.pillar_code} className="text-center p-2 rounded bg-cosmic-light/10 border border-divine-gold/10">
                              <p className="text-divine-gold font-heading text-sm">{p.pillar_code}</p>
                              <p className="text-chakra-crown/50 text-xs">{p.pillar_name}</p>
                              <p className="text-divine-gold font-heading text-xl mt-1">{p.trigger_count}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Dynamic Kosha Quiz Results */}
                      <div className="sacred-card" data-testid="analytics-kosha">
                        <KoshaQuizResultsView token={token} clientId={selectedClient?.user_id} quizType="relationship" />
                      </div>
                    </div>
                  ) : (
                    <div className="sacred-card flex items-center justify-center h-96">
                      <div className="text-center">
                        <FlaskConical className="w-12 h-12 text-divine-gold/30 mx-auto mb-4" />
                        <p className="text-chakra-crown/50">Select a profiled client to review their calculation methodology</p>
                        <p className="text-chakra-crown/30 text-sm mt-2">This section shows formulas, data and conceptual statements for each metric</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports" className="space-y-6">
              <div className="sacred-card">
                <AdminReportsPanel token={token} />
              </div>
            </TabsContent>

            {/* ============ EXPERTS TAB ============ */}
            <TabsContent value="experts" className="space-y-6" data-testid="experts-tab-content">
              <Suspense fallback={<div className="p-8 text-center text-divine-gold/50 animate-pulse">Loading...</div>}>
                <AdminExpertsPanel token={token} experts={experts} clients={clients} expertClientMappings={expertClientMappings} practiceCategories={practiceCategories} fetchExperts={fetchExperts} fetchExpertClientMappings={fetchExpertClientMappings} />
              </Suspense>
            </TabsContent>

            <TabsContent value="practices" className="space-y-6" data-testid="practices-tab-content">
              <Suspense fallback={<div className="p-8 text-center text-divine-gold/50 animate-pulse">Loading...</div>}>
                <AdminPracticesPanel token={token} practices={practices} practiceCategories={practiceCategories} clients={clients} allPracticeAssignments={allPracticeAssignments} fetchPractices={fetchPractices} fetchAllPracticeAssignments={fetchAllPracticeAssignments} />
              </Suspense>
            </TabsContent>

            <TabsContent value="settings">
              <Suspense fallback={<div className="p-8 text-center text-divine-gold/50 animate-pulse">Loading...</div>}>
                <AdminSettingsPanel token={token} />
              </Suspense>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="bg-cosmic-deep border border-divine-gold/20 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-divine-gold">Client Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <h4 className="label-sacred mb-3">CORE IDENTITY</h4>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Full Name", value: clientDetail?.client?.full_name || clientDetail?.client?.name },
                  { label: "Email", value: clientDetail?.client?.email },
                  { label: "Mobile", value: clientDetail?.client?.mobile },
                  { label: "City", value: clientDetail?.client?.city || "Not specified" },
                  { label: "Age Group", value: clientDetail?.client?.age_group || "Not specified" },
                  { label: "Gender", value: clientDetail?.client?.gender || "Not specified" },
                ].map(f => (
                  <div key={f.label}>
                    <p className="text-chakra-crown/60 text-sm">{f.label}</p>
                    <p className="text-chakra-crown">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="label-sacred mb-3">RELATIONAL CONTEXT</h4>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Relationship Status", value: clientDetail?.client?.relationship_status || "Not specified" },
                  { label: "Marriage Type", value: clientDetail?.client?.marriage_type || "Not applicable" },
                  { label: "Duration", value: clientDetail?.client?.relationship_duration || "Not specified" },
                  { label: "Living Situation", value: clientDetail?.client?.living_situation || "Not specified" },
                ].map(f => (
                  <div key={f.label}>
                    <p className="text-chakra-crown/60 text-sm">{f.label}</p>
                    <p className="text-chakra-crown">{f.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Client Modal */}
      <Dialog open={!!editingClient} onOpenChange={(open) => { if (!open) setEditingClient(null); }}>
        <DialogContent className="bg-cosmic-deep border border-divine-gold/20 max-w-lg" data-testid="edit-client-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-divine-gold">Edit Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-chakra-crown/60 text-xs block mb-1">Name</label>
                <Input value={editClientForm.name || ""} onChange={e => setEditClientForm(f => ({...f, name: e.target.value}))}
                  className="bg-neutral-obsidian/50 border-divine-gold/20 text-chakra-crown text-sm" data-testid="edit-client-name" />
              </div>
              <div>
                <label className="text-chakra-crown/60 text-xs block mb-1">Client ID</label>
                <Input value={editClientForm.client_id || ""} onChange={e => setEditClientForm(f => ({...f, client_id: e.target.value}))}
                  className="bg-neutral-obsidian/50 border-divine-gold/20 text-chakra-crown text-sm font-mono" data-testid="edit-client-id" />
              </div>
            </div>
            <div>
              <label className="text-chakra-crown/60 text-xs block mb-1">Email</label>
              <Input value={editClientForm.email || ""} onChange={e => setEditClientForm(f => ({...f, email: e.target.value}))}
                className="bg-neutral-obsidian/50 border-divine-gold/20 text-chakra-crown text-sm" data-testid="edit-client-email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-chakra-crown/60 text-xs block mb-1">Mobile</label>
                <Input value={editClientForm.mobile || ""} onChange={e => setEditClientForm(f => ({...f, mobile: e.target.value}))}
                  className="bg-neutral-obsidian/50 border-divine-gold/20 text-chakra-crown text-sm" data-testid="edit-client-mobile" />
              </div>
              <div>
                <label className="text-chakra-crown/60 text-xs block mb-1">Country</label>
                <Input value={editClientForm.country || ""} onChange={e => setEditClientForm(f => ({...f, country: e.target.value}))}
                  className="bg-neutral-obsidian/50 border-divine-gold/20 text-chakra-crown text-sm" data-testid="edit-client-country" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-chakra-crown/60 text-xs block mb-1">Region</label>
                <select value={editClientForm.client_region || ""} onChange={e => setEditClientForm(f => ({...f, client_region: e.target.value}))}
                  className="w-full rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown px-3 py-2 text-sm" data-testid="edit-client-region">
                  <option value="domestic">Domestic</option>
                  <option value="international">International</option>
                </select>
              </div>
              <div>
                <label className="text-chakra-crown/60 text-xs block mb-1">Subscription</label>
                <select value={editClientForm.subscription_status || "none"} onChange={e => setEditClientForm(f => ({...f, subscription_status: e.target.value}))}
                  className="w-full rounded-lg bg-neutral-obsidian/50 border border-divine-gold/20 text-chakra-crown px-3 py-2 text-sm" data-testid="edit-client-subscription">
                  <option value="none">None</option>
                  <option value="premium">Premium</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-chakra-crown/60 text-xs block mb-1">Reset Password (leave blank to keep current)</label>
              <Input type="password" value={editClientForm.password || ""} onChange={e => setEditClientForm(f => ({...f, password: e.target.value}))}
                placeholder="Min 8 characters" className="bg-neutral-obsidian/50 border-divine-gold/20 text-chakra-crown text-sm" data-testid="edit-client-password" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setEditingClient(null)}
                className="border-divine-gold/20 text-chakra-crown/60 text-xs">Cancel</Button>
              <Button size="sm" onClick={handleSaveClient} disabled={editClientSaving}
                className="bg-divine-gold text-cosmic-void hover:bg-divine-gold-light text-xs" data-testid="save-edit-client-btn">
                {editClientSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
